import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import CasinoIcon from "@mui/icons-material/Casino";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  PALETTE_STORAGE_KEY,
  RANDOM_PALETTE_ID,
  RANDOM_PALETTE_STORAGE_KEY,
  activePaletteId,
  generateRandomPalette,
  paletteIds,
  palettes,
} from "./tokens";

/**
 * Admin-only palette switcher. Renders nothing unless the current user
 * is an admin (Redux `auth.is_admin`). Lets admins cycle through
 * palettes, generate random palettes, and copy the current palette
 * to clipboard.
 */
export default function PaletteSwitcher() {
  const isAdmin = useSelector((s) => s.auth?.is_admin);
  const [anchorEl, setAnchorEl] = useState(null);
  const [current, setCurrent] = useState(activePaletteId);
  const [toast, setToast] = useState("");

  // Keep label in sync if another tab changes the palette.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PALETTE_STORAGE_KEY && e.newValue) setCurrent(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!isAdmin) return null;

  const handleSelect = (id) => {
    window.localStorage.setItem(PALETTE_STORAGE_KEY, id);
    const url = new URL(window.location.href);
    url.searchParams.set("palette", id);
    window.location.replace(url.toString());
  };

  const handleRandomize = () => {
    const random = generateRandomPalette();
    window.localStorage.setItem(
      RANDOM_PALETTE_STORAGE_KEY,
      JSON.stringify(random),
    );
    window.localStorage.setItem(PALETTE_STORAGE_KEY, RANDOM_PALETTE_ID);
    const url = new URL(window.location.href);
    url.searchParams.set("palette", RANDOM_PALETTE_ID);
    window.location.replace(url.toString());
  };

  const handleCopy = async () => {
    const p = palettes[current];
    if (!p) return;
    // Strip internal `_meta` and emit a JS object literal (unquoted keys,
    // named entry) so the result can be pasted directly into palettes.js.
    const { _meta, ...clean } = p;
    const name =
      current === RANDOM_PALETTE_ID
        ? _meta
          ? `${_meta.mood}${cap(_meta.bgStyle)}${_meta.h}`
          : `random${Math.floor(Math.random() * 360)}`
        : current;
    const body = Object.entries(clean)
      .map(([k, v]) => `    ${k}: "${v}",`)
      .join("\n");
    const text = `  ${name}: {\n${body}\n  },`;
    try {
      await navigator.clipboard.writeText(text);
      setToast("Palette copied to clipboard");
    } catch {
      setToast("Copy failed");
    }
    setAnchorEl(null);
  };

  const randomMeta =
    current === RANDOM_PALETTE_ID ? palettes[RANDOM_PALETTE_ID]?._meta : null;
  const tooltipLabel = randomMeta
    ? `Random · ${randomMeta.scheme} · ${randomMeta.mood} · ${randomMeta.bgStyle} (h=${randomMeta.h})`
    : `Palette: ${current}`;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: (t) => t.zIndex.tooltip + 1,
      }}
    >
      <Tooltip title={tooltipLabel} arrow>
        <IconButton
          aria-label="Switch color palette"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            bgcolor: "background.paper",
            boxShadow: 3,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem onClick={handleRandomize} sx={{ gap: 1.5, minWidth: 220 }}>
          <CasinoIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Randomize palette
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleCopy} sx={{ gap: 1.5, minWidth: 220 }}>
          <ContentCopyIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Copy current palette
          </Typography>
        </MenuItem>
        <Divider />
        {paletteIds.map((id) => {
          const p = palettes[id];
          const selected = id === current;
          const label = id === RANDOM_PALETTE_ID ? "random (saved)" : id;
          return (
            <MenuItem
              key={id}
              selected={selected}
              onClick={() => handleSelect(id)}
              sx={{ gap: 1.5, minWidth: 220 }}
            >
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Swatch color={p.primary} />
                <Swatch color={p.accent} />
                <Swatch color={p.background} border />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: selected ? 700 : 400 }}
              >
                {label}
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

function Swatch({ color, border }) {
  return (
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        bgcolor: color,
        border: border ? "1px solid rgba(0,0,0,0.15)" : "none",
      }}
    />
  );
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}
